import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOCALES, LOCALE_LABELS, useI18n, type Locale } from "../../i18n";
import { useBoundStore } from "../../store";
import { useWorkspace } from "../../workspace/WorkspaceProvider";
import { changeOwnPassword, updateOwnName } from "../../services/User";

type Errors = { errors?: Array<{ title?: string; detail?: string }> };
const errorText = (res: Errors, fallback: string) => res.errors?.[0]?.detail || res.errors?.[0]?.title || fallback;

/** Everyone: their own name, password and language. */
const AccountPage: React.FC = () => {
  const { t, locale, setLocale } = useI18n();
  const a = t.account;
  const user = useBoundStore((state) => state.user);
  const setUser = useBoundStore((state) => state.setUser);
  const { displayName } = useWorkspace();

  const [firstName, setFirstName] = useState(user?.firstName ?? "");
  const [lastName, setLastName] = useState(user?.lastName ?? "");
  const [savingName, setSavingName] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    setFirstName(user?.firstName ?? "");
    setLastName(user?.lastName ?? "");
  }, [user?.firstName, user?.lastName]);

  const nameChanged =
    firstName.trim() !== (user?.firstName ?? "") || lastName.trim() !== (user?.lastName ?? "");

  const saveName = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingName(true);
    try {
      const res = await updateOwnName(firstName.trim(), lastName.trim());
      if (res.success && res.data) {
        setUser(res.data);
        toast.success(a.profile.saved);
      } else {
        toast.error(errorText(res, a.failed));
      }
    } finally {
      setSavingName(false);
    }
  };

  const savePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error(a.password.mismatch);
      return;
    }
    setSavingPassword(true);
    try {
      const res = await changeOwnPassword(current, next);
      if (res.success) {
        toast.success(a.password.saved);
        setCurrent("");
        setNext("");
        setConfirm("");
      } else if (res.errors?.[0]?.title === "Wrong password") {
        toast.error(a.password.wrongCurrent);
      } else {
        toast.error(errorText(res, a.failed));
      }
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="w-full max-w-2xl space-y-6 p-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{a.title}</h1>
        <p className="text-muted-foreground">{a.subtitle}</p>
      </div>

      <Card className="p-5">
        <h2 className="mb-4 text-base font-semibold">{a.profile.title}</h2>
        <form className="space-y-4" onSubmit={(e) => void saveName(e)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="account-first">{a.profile.firstName}</Label>
              <Input id="account-first" maxLength={100} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="account-last">{a.profile.lastName}</Label>
              <Input id="account-last" maxLength={100} value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="account-email">{a.profile.email}</Label>
            <Input id="account-email" value={user?.email ?? ""} disabled />
            <p className="text-xs text-muted-foreground">{a.profile.emailHint}</p>
          </div>
          {(displayName || user?.organization?.name) && (
            <div className="text-sm">
              <span className="text-muted-foreground">{a.profile.organization}: </span>
              {displayName || user?.organization?.name}
            </div>
          )}
          <Button type="submit" disabled={savingName || !nameChanged || !firstName.trim() || !lastName.trim()}>
            {savingName && <Loader2 className="h-4 w-4 animate-spin" />}
            {a.profile.save}
          </Button>
        </form>
      </Card>

      <Card className="p-5">
        <h2 className="mb-4 text-base font-semibold">{a.password.title}</h2>
        <form className="space-y-4" onSubmit={(e) => void savePassword(e)}>
          <div className="space-y-1.5">
            <Label htmlFor="account-current">{a.password.current}</Label>
            <Input id="account-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="account-new">{a.password.next}</Label>
              <Input id="account-new" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="account-confirm">{a.password.confirm}</Label>
              <Input id="account-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{a.password.hint}</p>
          <Button type="submit" disabled={savingPassword || !current || next.length < 8 || !confirm}>
            {savingPassword && <Loader2 className="h-4 w-4 animate-spin" />}
            {a.password.save}
          </Button>
        </form>
      </Card>

      <Card className="p-5">
        <h2 className="text-base font-semibold">{a.language.title}</h2>
        <p className="mb-3 text-sm text-muted-foreground">{a.language.hint}</p>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={a.language.title}>
          {LOCALES.map((code: Locale) => (
            <Button
              key={code}
              type="button"
              role="radio"
              aria-checked={locale === code}
              variant={locale === code ? "default" : "outline"}
              onClick={() => setLocale(code)}
            >
              <span aria-hidden>{LOCALE_LABELS[code].flag}</span>
              {LOCALE_LABELS[code].label}
            </Button>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default AccountPage;
